import { createSelector } from "reselect";

import { type AppState, UsePortalSelector } from "../index";
import { selectQuoteAccessories } from "./accessories/hooks";
import { selectQuoteAwnings } from "./awnings/hooks";

/**
 * Accessories and awnings both live in `quotes__accessories` on the backend and therefore share a
 * single line number sequence. The backend keys item pricing by (quote_id, line_number), so handing
 * it a line number that is already taken by the *other* collection makes one row's pricing overwrite
 * the other's. Always derive the next line number from the union of both collections.
 */
export const selectNextQuoteLineNumber = (quoteId: number) => createSelector(
	selectQuoteAccessories(quoteId),
	selectQuoteAwnings(quoteId),
	(accessories, awnings) => [...accessories, ...awnings]
		.reduce((max, item) => Math.max(max, Math.abs(item.line_number)), 0) + 1
);

/**
 * Non-hook variant for call sites that need a freshly computed value mid-loop (e.g. duplicating an
 * awning several times in a row) or that live outside of React.
 */
export const getNextQuoteLineNumber = (quoteId: number, state: AppState) =>
	selectNextQuoteLineNumber(quoteId)(state);

export const useNextQuoteLineNumber = (quoteId: number) =>
	UsePortalSelector(selectNextQuoteLineNumber(quoteId));
